import { z } from 'zod';
import { MoneySchema } from './common.js';

/**
 * A client's own carrier rate sheet (0131, owner 26 September 2026): what THEIR carrier charges them
 * per call, shown on each call over their own trunk. Never charged to the ArcNet balance.
 *
 * Carriers send these as spreadsheets in every shape, so the parsing lives here, shared: the portal
 * previews exactly what the API will store.
 */

export const OwnRateRuleSchema = z.object({
  prefix: z.string().regex(/^\d{1,15}$/, 'digits only, international form without the plus'),
  description: z.string().max(200).optional(),
  rate_per_minute: MoneySchema,
  connection_fee: MoneySchema.default('0'),
  first_increment_sec: z.number().int().min(1).max(3600).default(1),
  subsequent_increment_sec: z.number().int().min(1).max(3600).default(1),
}).strict();
export type OwnRateRule = z.infer<typeof OwnRateRuleSchema>;

export const OwnRateSheetUploadSchema = z.object({
  rules: z.array(OwnRateRuleSchema).min(1).max(20000),
  /** The file it came from, for the record. */
  file_name: z.string().max(200).optional(),
}).strict();

export interface OwnRateSheet {
  deck_id: string;
  currency: string;
  version: number;
  effective_from: string;
  rule_count: number;
  file_name: string | null;
}

export interface ParsedRateSheet {
  rules: OwnRateRule[];
  /** One line per row that was skipped, and why; the first 20. */
  problems: string[];
  skipped: number;
  /** Which column each field came from, by header text. */
  columns: { prefix: string; rate: string; description: string | null; connection: string | null; increments: string | null };
}

/** Comma- or semicolon-separated, quotes honoured. Spreadsheets save CSV either way round. */
export function parseCsvRows(text: string): string[][] {
  const clean = text.replace(/^﻿/, '');
  // Judged on the first lines together, not the first: carriers put a title line above the table.
  const head = clean.split(/\r?\n/, 15).join('\n');
  const n = (re: RegExp) => head.match(re)?.length ?? 0;
  const sep = n(/;/g) > n(/,/g) && n(/;/g) >= n(/\t/g) ? ';' : n(/\t/g) > n(/,/g) ? '\t' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i]!;
    if (quoted) {
      if (ch === '"' && clean[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && clean[i + 1] === '\n') i++;
      row.push(cell); cell = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  return rows;
}

const HEAD = {
  prefix: /^(prefix|dial ?code|code|dialling ?code|area ?code|number ?prefix|destination ?code)$/i,
  rate: /(rate|price|cost|tariff|per ?min)/i,
  perSecond: /(per ?sec|\/ ?s(ec)?\b)/i,
  description: /^(description|destination|name|country|area|zone|route)$/i,
  connection: /(connect|setup|set-up|connection)/i,
  increments: /(increment|billing|interval|pulse)/i,
};

/** "R0,35", "0.35 ZAR", "$0.02" -> "0.35"; null when it is not a number. */
export function sheetMoney(raw: string): string | null {
  const s = raw.trim().replace(/[^\d.,-]/g, '');
  if (!s) return null;
  // A comma is a decimal point when there is no dot and it has 1-6 digits after it.
  const n = /^-?\d+,\d{1,6}$/.test(s) ? s.replace(',', '.') : s.replace(/,/g, '');
  if (!/^\d+(\.\d+)?$/.test(n)) return null;
  const [whole, frac = ''] = n.split('.');
  return frac ? `${whole}.${frac.slice(0, 6)}` : whole!;
}

/**
 * A prefix in international form. "+27 11", "002711" and "2711" are the same; a national "011" takes
 * the account's country code, since calls are rated on their international form.
 */
export function internationalPrefix(raw: string, countryCode: string): string | null {
  let d = raw.trim();
  const plus = d.startsWith('+');
  d = d.replace(/\D/g, '');
  if (!d) return null;
  if (!plus && d.startsWith('00')) d = d.slice(2);
  else if (!plus && d.startsWith('0')) d = countryCode + d.slice(1);
  return /^\d{1,15}$/.test(d) ? d : null;
}

/** "1/1", "60/60", "30 / 6", "60" -> first and next increment in seconds. */
function increments(raw: string): { first: number; next: number } | null {
  const m = /^\s*(\d{1,4})\s*(?:[/+:x-]\s*(\d{1,4}))?\s*s?\s*$/i.exec(raw);
  if (!m) return null;
  const first = Number(m[1]); const next = Number(m[2] ?? m[1]);
  return first >= 1 && next >= 1 && first <= 3600 && next <= 3600 ? { first, next } : null;
}

/**
 * Rows of a sheet into rules. The header row is found, not assumed (carriers put a title and a date
 * above it), and a column is recognised by its header. Rows that cannot be read are skipped and
 * listed; a prefix that appears twice keeps its first rate.
 */
export function parseRateSheet(rows: string[][], countryCode = '27'): ParsedRateSheet | { error: string } {
  const headerAt = rows.slice(0, 15).findIndex((r) =>
    r.some((c) => HEAD.prefix.test(c.trim())) && r.some((c) => HEAD.rate.test(c.trim())));
  if (headerAt < 0) {
    return { error: 'No header row with a prefix (or dial code) column and a rate column was found in the first 15 rows.' };
  }
  const header = rows[headerAt]!.map((c) => c.trim());
  const find = (re: RegExp, not?: number[]) => header.findIndex((c, i) => re.test(c) && !(not ?? []).includes(i));
  const iPrefix = find(HEAD.prefix);
  const iConn = find(HEAD.connection);
  const iInc = find(HEAD.increments);
  // Or the two apart: "first increment" and "next/subsequent increment" columns.
  const iFirst = header.findIndex((c) => /first|initial|minimum/i.test(c) && HEAD.increments.test(c));
  const iNext = header.findIndex((c) => /subsequent|next|additional|then/i.test(c) && HEAD.increments.test(c));
  const split = iFirst >= 0 && iNext >= 0;
  // The rate column is the first rate-like one that is not the connection fee.
  const iRate = header.findIndex((c, i) => HEAD.rate.test(c) && i !== iConn && i !== iPrefix && i !== iInc && i !== iFirst && i !== iNext);
  const iDesc = find(HEAD.description, [iPrefix]);
  if (iRate < 0) return { error: 'No rate column was found beside the prefix column.' };
  const perSecond = HEAD.perSecond.test(header[iRate]!);

  const rules: OwnRateRule[] = [];
  const problems: string[] = [];
  const seen = new Set<string>();
  let skipped = 0;
  const skip = (line: number, why: string) => { skipped++; if (problems.length < 20) problems.push(`Row ${line}: ${why}`); };

  for (let r = headerAt + 1; r < rows.length; r++) {
    const row = rows[r]!;
    const line = r + 1;
    const rawPrefix = row[iPrefix] ?? '';
    if (!rawPrefix.trim() && !(row[iRate] ?? '').trim()) continue;
    const prefix = internationalPrefix(rawPrefix, countryCode);
    if (!prefix) { skip(line, `prefix "${rawPrefix.trim()}" is not a number`); continue; }
    let rate = sheetMoney(row[iRate] ?? '');
    if (rate === null) { skip(line, `rate "${(row[iRate] ?? '').trim()}" is not a number`); continue; }
    if (perSecond) rate = (Number(rate) * 60).toFixed(6).replace(/\.?0+$/, '');
    if (seen.has(prefix)) { skip(line, `prefix ${prefix} already listed; the first rate is kept`); continue; }
    seen.add(prefix);
    const inc = split
      ? (() => {
          const f = Number((row[iFirst] ?? '').trim()); const n = Number((row[iNext] ?? '').trim());
          return Number.isInteger(f) && Number.isInteger(n) && f >= 1 && n >= 1 && f <= 3600 && n <= 3600 ? { first: f, next: n } : null;
        })()
      : iInc >= 0 ? increments(row[iInc] ?? '') : null;
    const conn = iConn >= 0 ? sheetMoney(row[iConn] ?? '') : null;
    const desc = iDesc >= 0 ? (row[iDesc] ?? '').trim().slice(0, 200) : '';
    rules.push({
      prefix, rate_per_minute: rate, connection_fee: conn ?? '0',
      first_increment_sec: inc?.first ?? 1, subsequent_increment_sec: inc?.next ?? 1,
      ...(desc ? { description: desc } : {}),
    });
  }
  return {
    rules, problems, skipped,
    columns: {
      prefix: header[iPrefix]!, rate: header[iRate]! + (perSecond ? ' (per second, shown per minute)' : ''),
      description: iDesc >= 0 ? header[iDesc]! : null,
      connection: iConn >= 0 ? header[iConn]! : null,
      increments: split ? `${header[iFirst]!} and ${header[iNext]!}` : iInc >= 0 ? header[iInc]! : null,
    },
  };
}
